/**
 * Session Monitor Hook
 * GW-4620: Monitors active session status with polling and countdown
 *
 * Polls sessionsApi.getSession() every 15 seconds and counts down locally
 * between polls. Derives status based on remaining time thresholds.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

import { SessionStatus, type SessionStatusType } from '@gw/ui/types';

import { sessionsApi } from '@/api/sessions';

import type { ApplicationSession } from '@gw/ui/types';

type SessionMonitorStatus =
  | 'idle' // Not monitoring any session
  | 'active' // > 300s remaining
  | 'warning' // 60-300s remaining
  | 'critical' // < 60s remaining
  | 'expired' // <= 0s remaining
  | 'ended' // Session status is expired/revoked
  | 'offline'; // 3+ consecutive API failures

export interface UseSessionMonitorReturn {
  session: ApplicationSession | null;
  remainingSeconds: number;
  status: SessionMonitorStatus;
  isPolling: boolean;
  failureCount: number;
  startMonitoring: (session: ApplicationSession) => void;
  stopMonitoring: () => void;
  refreshNow: () => Promise<void>;
}

export function useSessionMonitor(): UseSessionMonitorReturn {
  const [session, setSession] = useState<ApplicationSession | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [status, setStatus] = useState<SessionMonitorStatus>('idle');
  const [isPolling, setIsPolling] = useState(false);
  const [failureCount, setFailureCount] = useState(0);

  // Refs to track intervals and prevent stale closures
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentSessionIdRef = useRef<string | null>(null);
  const requestEpochRef = useRef(0); // Stale poll guard counter
  const currentStatusRef = useRef<SessionMonitorStatus>('idle'); // Track current status for countdown

  // Calculate remaining seconds from expiry time
  const calculateRemainingSeconds = useCallback((expiresAt: string): number => {
    const expiryTime = new Date(expiresAt).getTime();
    const now = Date.now();
    const remaining = Math.max(0, Math.floor((expiryTime - now) / 1000));
    return remaining;
  }, []);

  // Derive status from remaining seconds and failure count
  const deriveStatus = useCallback(
    (
      remainingSeconds: number,
      sessionStatus: SessionStatusType,
      failureCount: number
    ): SessionMonitorStatus => {
      if (failureCount >= 3) return 'offline';
      if (sessionStatus === SessionStatus.Expired || sessionStatus === SessionStatus.Revoked)
        return 'ended';
      if (remainingSeconds <= 0) return 'expired';
      if (remainingSeconds < 60) return 'critical';
      if (remainingSeconds <= 300) return 'warning';
      return 'active';
    },
    []
  );

  // Stop monitoring — defined first so fetchSessionData and startMonitoring can reference it
  const stopMonitoring = useCallback((clearSession = true) => {
    setIsPolling(false);
    currentSessionIdRef.current = null;

    if (clearSession) {
      setSession(null);
      setRemainingSeconds(0);
      setStatus('idle');
      currentStatusRef.current = 'idle'; // Keep ref in sync
    }

    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Fetch session data from API
  const fetchSessionData = useCallback(
    async (sessionId: string): Promise<void> => {
      // Capture current epoch at start of request for stale poll guard
      const epoch = requestEpochRef.current;

      try {
        const updatedSession = await sessionsApi.getSession(sessionId);

        // Stale poll guard: discard response if epoch changed (manual action occurred)
        if (epoch !== requestEpochRef.current) {
          return; // Discard stale response
        }

        const remaining = calculateRemainingSeconds(updatedSession.expiresAt);
        const newStatus = deriveStatus(remaining, updatedSession.status, 0);

        setSession(updatedSession);
        setRemainingSeconds(remaining);
        setStatus(newStatus);
        currentStatusRef.current = newStatus; // Keep ref in sync
        setFailureCount(0); // Reset failure count on successful fetch

        // Auto-stop polling if session is ended/expired from API (keep session data)
        if (
          updatedSession.status === SessionStatus.Expired ||
          updatedSession.status === SessionStatus.Revoked
        ) {
          stopMonitoring(false); // Don't clear session data
        }
      } catch (error) {
        console.warn('Failed to fetch session data:', error);
        setFailureCount((prev) => {
          const newCount = prev + 1;
          if (newCount >= 3) {
            setStatus('offline');
            currentStatusRef.current = 'offline'; // Keep ref in sync
            // Fix: Stop polling when offline but keep countdown running
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
          }
          return newCount;
        });
      }
    },
    [calculateRemainingSeconds, deriveStatus, stopMonitoring]
  );

  // Start monitoring a session
  const startMonitoring = useCallback(
    (initialSession: ApplicationSession) => {
      // Stop any existing monitoring
      stopMonitoring(true);

      setSession(initialSession);
      setIsPolling(true);
      setFailureCount(0);
      currentSessionIdRef.current = initialSession.id;

      const remaining = calculateRemainingSeconds(initialSession.expiresAt);
      const initialStatus = deriveStatus(remaining, initialSession.status, 0);
      setRemainingSeconds(remaining);
      setStatus(initialStatus);
      currentStatusRef.current = initialStatus; // Keep ref in sync

      // Start polling every 15 seconds
      pollIntervalRef.current = setInterval(() => {
        if (currentSessionIdRef.current) {
          fetchSessionData(currentSessionIdRef.current);
        }
      }, 15000);

      // Start countdown every 1 second
      countdownIntervalRef.current = setInterval(() => {
        setRemainingSeconds((prev) => {
          const newRemaining = Math.max(0, prev - 1);

          // Update status based on new remaining time, but respect offline status
          // Don't use nested setState - check current status via ref
          if (currentStatusRef.current !== 'offline') {
            // For countdown, only time matters (session status changes come from API)
            let newStatus: SessionMonitorStatus;
            if (newRemaining <= 0) newStatus = 'expired';
            else if (newRemaining < 60) newStatus = 'critical';
            else if (newRemaining <= 300) newStatus = 'warning';
            else newStatus = 'active';

            // Only update if status actually changed to avoid unnecessary re-renders
            if (newStatus !== currentStatusRef.current) {
              setStatus(newStatus);
              currentStatusRef.current = newStatus;
            }
          }

          return newRemaining;
        });
      }, 1000);

      // Immediate initial fetch
      fetchSessionData(initialSession.id);
    },
    [calculateRemainingSeconds, deriveStatus, fetchSessionData, stopMonitoring]
  );

  // Manually refresh session data
  const refreshNow = useCallback(async (): Promise<void> => {
    if (currentSessionIdRef.current) {
      // Increment epoch to mark manual action (invalidates pending requests)
      requestEpochRef.current += 1;
      await fetchSessionData(currentSessionIdRef.current);
    }
  }, [fetchSessionData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  return {
    session,
    remainingSeconds,
    status,
    isPolling,
    failureCount,
    startMonitoring,
    stopMonitoring,
    refreshNow,
  };
}
